import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Rise } from "./Reveal";
import { CATEGORIES } from "../../lib/products";
import { useLang } from "../../lib/LangContext";
import { localizedCategory } from "../../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { dict, isRTL } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    category: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(dict.toast_missing);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      toast.success(dict.toast_success);
      setForm({ name: "", email: "", company: "", category: "", message: "" });
    } catch (err) {
      toast.error(dict.toast_error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#F7F5F0] py-28 md:py-40 border-t hairline"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-5">
          <span className="label text-[#1A1918]">{dict.contact_kicker}</span>
          <h2 className="mt-8 serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.02em] text-[#1A1918]">
            {dict.contact_title_1} <span className="italic">{dict.contact_title_2}</span>
          </h2>
          <p className="mt-8 text-[16px] leading-[1.7] text-[#5C5A55] max-w-[46ch]">
            {dict.contact_body}
          </p>

          <div className="mt-16 grid grid-cols-2 gap-6">
            {[
              [dict.contact_studio, dict.contact_studio_v],
              [dict.contact_hours, dict.contact_hours_v],
              [dict.contact_trade, dict.contact_trade_v],
              [dict.contact_reach, dict.contact_reach_v],
            ].map(([k, v], i) => (
              <div key={i}>
                <div className="label text-[10px] mb-2 text-[#5C5A55]">{k}</div>
                <div className="serif text-xl text-[#1A1918]">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <Rise className="col-span-12 md:col-span-6 md:col-start-7">
          <form onSubmit={submit} data-testid="inquiry-form" className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <label className="label text-[10px] text-[#5C5A55]">{dict.field_name}</label>
                <input
                  data-testid="inquiry-name"
                  className="field"
                  value={form.name}
                  onChange={update("name")}
                  placeholder={dict.field_name_ph}
                  required
                />
              </div>
              <div>
                <label className="label text-[10px] text-[#5C5A55]">{dict.field_email}</label>
                <input
                  data-testid="inquiry-email"
                  className="field"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder={dict.field_email_ph}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mt-6">
              <div>
                <label className="label text-[10px] text-[#5C5A55]">{dict.field_company}</label>
                <input
                  data-testid="inquiry-company"
                  className="field"
                  value={form.company}
                  onChange={update("company")}
                  placeholder={dict.field_company_ph}
                />
              </div>
              <div>
                <label className="label text-[10px] text-[#5C5A55]">{dict.field_category}</label>
                <select
                  data-testid="inquiry-category"
                  className="field"
                  value={form.category}
                  onChange={update("category")}
                >
                  <option value="">{dict.field_category_ph}</option>
                  {CATEGORIES.map((c) => {
                    const loc = localizedCategory(dict, c.id);
                    return (
                      <option key={c.id} value={c.title}>
                        {loc.title}
                      </option>
                    );
                  })}
                  <option value="Mixed / All">{dict.field_category_all}</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="label text-[10px] text-[#5C5A55]">{dict.field_message}</label>
              <textarea
                data-testid="inquiry-message"
                className="field"
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder={dict.field_message_ph}
                required
              />
            </div>

            <div className="mt-10 flex items-center justify-between">
              <span className="label text-[10px] text-[#5C5A55]">
                {sent ? dict.form_note_sent : dict.form_note_idle}
              </span>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit"
                className="inline-flex items-center gap-3 px-6 py-4 bg-[#1A1918] text-[#F7F5F0] label text-[10px] hover:bg-[#B56B45] transition-colors duration-500 disabled:opacity-60"
              >
                {submitting ? dict.form_submit_sending : dict.form_submit_idle}
                <span aria-hidden>{isRTL ? "←" : "→"}</span>
              </button>
            </div>
          </form>
        </Rise>
      </div>
    </section>
  );
}
